// src/api/analyticsApi.js
import api from "./api";

/**
 * Analytics API service
 * Provides methods for accessing advanced analytics endpoints
 */

// ==================== DEPARTMENT ANALYTICS ====================

/**
 * Get department performance comparison
 * @param {number} periodDays - Number of days to analyze (default: 30)
 * @returns {Promise} Department performance comparison data
 */
export const getDepartmentPerformance = async (periodDays = 30) => {
  const response = await api.get("/analytics/departments/performance", {
    params: { period_days: periodDays },
  });
  return response.data.data;
};

// ==================== TASK ANALYTICS ====================

/**
 * Get task rejection analysis
 * @param {number} periodDays - Number of days to analyze (default: 30)
 * @param {number} topN - Number of top rejected tasks to return (default: 10)
 * @returns {Promise} Task rejection analysis with top rejected tasks and reasons
 */
export const getTaskRejections = async (periodDays = 30, topN = 10) => {
  const response = await api.get("/analytics/tasks/rejections", {
    params: { period_days: periodDays, top_n: topN },
  });
  return response.data.data;
};

/**
 * Get task trends over time
 * @param {string} period - Time period: "daily", "weekly", or "monthly" (default: "daily")
 * @param {number} days - Number of days to include (default: 30)
 * @returns {Promise} Task trends data points
 */
export const getTaskTrends = async (period = "daily", days = 30) => {
  const response = await api.get("/analytics/tasks/trends", {
    params: { period, days },
  });
  return response.data.data;
};

/**
 * Get task complexity metrics
 * @param {number} periodDays - Number of days to analyze (default: 30)
 * @returns {Promise} Task complexity distribution
 */
export const getTaskComplexity = async (periodDays = 30) => {
  const response = await api.get("/analytics/tasks/complexity", {
    params: { period_days: periodDays },
  });
  return response.data.data;
};

// ==================== WORKLOAD ANALYTICS ====================

/**
 * Get workload distribution across users
 * @returns {Promise} Workload distribution with user status categorization
 */
export const getWorkloadDistribution = async () => {
  const response = await api.get("/analytics/workload/distribution");
  return response.data.data;
};

// ==================== EVENT ANALYTICS ====================

/**
 * Get event engagement metrics
 * @param {number} periodDays - Number of days to analyze (default: 30)
 * @returns {Promise} Event engagement summary
 */
export const getEventEngagement = async (periodDays = 30) => {
  const response = await api.get("/analytics/events/engagement", {
    params: { period_days: periodDays },
  });
  return response.data.data;
};

// ==================== APPROVAL ANALYTICS ====================

/**
 * Get approval chain analysis
 * @returns {Promise} Approval chain performance metrics
 */
export const getApprovalChainAnalysis = async () => {
  const response = await api.get("/analytics/approval/chain-analysis");
  return response.data.data;
};

// ==================== TIME ANALYTICS ====================

/**
 * Get time-based activity analysis
 * @param {number} periodDays - Number of days to analyze (default: 30)
 * @returns {Promise} Time analysis with hourly and daily distribution
 */
export const getTimeAnalysis = async (periodDays = 30) => {
  const response = await api.get("/analytics/time-analysis", {
    params: { period_days: periodDays },
  });
  return response.data.data;
};

// ==================== COMPREHENSIVE ANALYTICS ====================

/**
 * Get all analytics summary in one call
 * @param {number} periodDays - Number of days to analyze (default: 30)
 * @returns {Promise} Complete analytics summary
 */
export const getAnalyticsSummary = async (periodDays = 30) => {
  const response = await api.get("/analytics/summary", {
    params: { period_days: periodDays },
  });
  return response.data.data;
};

// ==================== USER PERFORMANCE ANALYTICS ====================

/**
 * Get detailed performance metrics for current user
 * @param {number} periodDays - Number of days to analyze (default: 30)
 * @returns {Promise} User performance details
 */
export const getMyPerformanceDetails = async (periodDays = 30) => {
  const response = await api.get("/analytics/my-performance-details", {
    params: { period_days: periodDays },
  });
  return response.data.data;
};
